import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { URLUtilities } from '../utilities/url.utilities';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { Injectable } from '@angular/core';
import { MapTileFormats } from '../enums/maps/mapTileFormats.enum';
import { MapTileUrlValidator } from '../utilities/maps/mapTileUrl.utilities';

@Injectable({
  providedIn: 'root',
})
export class WtValidators {

  // Predefined restricted character sets for consistency
  static readonly RESTRICTED_CHARS = {
    DEFAULT: [';', '!', '<', '>'],
  };

  constructor(private readonly translateService: TranslateService) {}

  textInput(minLength: number = 3, maxLength: number = 250, restrictedChars?: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value as string;

      if (!val) {
        return null;
      }

      const trimmedVal = val.trim();
      const isValidMinLength: boolean = trimmedVal.length >= minLength;
      const isValidMaxLength: boolean = val.length <= maxLength;

      const messages: string[] = [];

      if (!isValidMinLength) {
        messages.push(
          this.translateService.instant(T.common.minimum_length_is_count_symbols, {count: minLength})
        );
      }
      if (!isValidMaxLength) {
        messages.push(
          this.translateService.instant(T.common.maximum_length_is_count_symbols, {count: maxLength})
        );
      }

      if (val !== trimmedVal && trimmedVal.length >= minLength) {
        messages.push(
          this.translateService.instant(T.common.should_now_start_end_empty_space)
        );
      }

      if (restrictedChars && restrictedChars.length > 0) {
        const hasRestrictedChars: boolean = restrictedChars.some((char) => val.includes(char));

        if (hasRestrictedChars) {
          const matchedChars = restrictedChars.filter((char) => val.includes(char));
          const stringAsHtmlElement = restrictedChars.map((c) => {
            if (matchedChars.some((mc) => mc === c)) {
              return `<span class="highlight-error-msg">${c}</span>`;
            } else {
              return c;
            }
          });

          messages.push(
            this.translateService.instant(T.common.cant_contain_symbols, {symbols: stringAsHtmlElement.join(' ')})
          );
        }
      }

      return messages.length > 0 ? { titleValidator: { errorMessages: messages } } : null;
    };
  }

  restrictedChars(chars: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasRestrictedChars: boolean = chars.some((char) => (control?.value as string)?.includes(char));
      const messages: string[] = [];

      if (hasRestrictedChars) {
        const matchedChars = chars.filter((char) => (control.value as string).includes(char));

        const stringAsHtmlElement = chars.map((c) => {
          if (matchedChars.some((mc) => mc == c)) {
            return `<span class="highlight-error-msg">${c}</span>`;
          } else {
            return c;
          }
        });

        if (hasRestrictedChars) {
          messages.push(
            this.translateService.instant(T.common.cant_contain_symbols, {symbols: stringAsHtmlElement.join(' ')})
          )
        }
      }

      return hasRestrictedChars ? { restrictedChars: { errorMessages: messages } } : null;
    };
  }

  isNumeric(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const messages: string[] = [];
      const val = control.value as string;
      const isNotNumeric = isNaN(Number(val));

      if (isNotNumeric) {
        messages.push(
          this.translateService.instant(T.common.value_must_be_numeric)
          );
      }

      return !isNotNumeric ? null : { titleValidator: { errorMessages: messages } };
    };
  }

  // Check Empty space no mather where in the form
  emptySpace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasEmptySpace: boolean = (control?.value as string)?.includes(' ');
      const messages: string[] = [];
      if (hasEmptySpace) {
        messages.push(
          this.translateService.instant(T.common.cant_contain_empty_space)
          );
      }

      return hasEmptySpace ? { restrictedChars: { errorMessages: messages } } : null;
    };
  }

  validURL(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let isValidRegexp: boolean;

      if (control.value) {
        isValidRegexp = URLUtilities.isValidUrl(control.value);
        const messages: string[] = [];
        if (control.value?.length > 0 && !isValidRegexp) messages.push(this.translateService.instant(T.common.invalid_url));

        return isValidRegexp ? null : { titleValidator: { errorMessages: messages } };
      }

      return;
    };

  }

  htmlStrippedMaxLength(maxLength: number = 10000): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const strippedText = this.stripHtml(control.value);

      if (strippedText.length > maxLength) {
        return {
          htmlStrippedMaxLength: {
            requiredLength: maxLength,
            actualLength: strippedText.length,
            message: this.translateService.instant(T.common.description_error_message)
          }
        };
      }

      return null;
    };
  }

  /**
   * Creates an Angular validator function that checks if a control's value is a valid map tile URL
   * matching the specified tile scheme.
   *
   * @param expectedScheme - The expected map tile format scheme to validate against.
   * @returns A `ValidatorFn` that returns a validation error object if the URL does not match the expected scheme,
   *          or `null` if the value is valid or empty.
   *
   * @example
   * // Usage in a reactive form:
   * myFormControl = new FormControl('', [validMapTileURL('XYZ')]);
   */
  validMapTileURL(expectedScheme: MapTileFormats): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const url = control.value as string;
      const isValidMaptileScheme = MapTileUrlValidator.validate(url, expectedScheme);
      if (!isValidMaptileScheme) {
        return {
          validMapTileURL: {
            errorMessages: [
              this.translateService.instant(
                T.settings.location_manager.url_must_be_valid_tile_scheme, {tileScheme: MapTileFormats[expectedScheme]}
              )
            ]
          }
        };
      }

      return null; // Valid scheme
    };
  }

  validLatitude(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const val = control.value as number;

      const isValidLatitude: boolean = val >= -90 && val <= 90;
      if (!isValidLatitude) {
        return {
          validLatitude: {
            errorMessages: [this.translateService.instant(T.settings.location_manager.latitude_must_be_number_between)]
          }
        };
      }

      return null;
    };
  }

  validLongitude(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const val = control.value as number;

      const isValidLongitude: boolean = val >= -180 && val <= 180;
      if (!isValidLongitude) {
        return {
          validLongitude: {
            errorMessages: [this.translateService.instant(T.settings.location_manager.longitude_must_be_number_between)]
          }
        };
      }

      return null;
    };
  }

  /**
   * Strips HTML tags from a string
   * @param html String containing HTML tags
   * @returns String with HTML tags removed
   */
  private stripHtml(html: string): string {
    if (!html) return '';

    // First remove HTML tags
    const tagsRemoved = html.replace(/(<([^>]*)>)/gi, '');

    // Then decode HTML entities
    const tempElement = document.createElement('div');
    tempElement.innerHTML = tagsRemoved;

    // Get the text content which automatically decodes entities
    return tempElement.textContent || tempElement.innerText || '';
  }

}
